import React, {
  ReactNode, useContext, useEffect, useState,
} from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  Button, Card, Col, Container, Label, Row, Tooltip,
} from 'reactstrap';
import { Trans, useTranslation } from 'react-i18next';
import Dinero from 'dinero.js';
import { DeliveryMethod, useSellerGroupQuery } from '../../generated/graphql';
import Emoji from '../../components/Emoji';
import { CampaignContext } from '../../context/CampaignWrapper';
import SellerGroupSkeleton from './skeletons/SellerGroupSkeleton';
import SellerGroup from '../../utils/models/SellerGroup';
import { formatTimeslot, isDefined, SHORT_DATE_TIME } from '../../utils/helpers';
import ConfirmRemoveModal from './ConfirmRemoveModal';
import FacebookShareButton from '../../components/FacebookShareButton';
import Header from '../Main/components/Header';

interface OrderPropertyProps {
  label: string,
  children: ReactNode,
}

const OrderProperty = ({ children, label }: OrderPropertyProps) => (
  <Row key={label} className="mt-1">
    <Col lg={3}>
      <Label><strong>{label}</strong></Label>
    </Col>
    <Col>{children}</Col>
  </Row>
);

const SellerGroupView = () => {
  const [isCopied, setIsCopied] = useState(false);
  const { sellerGroupSecret } = useParams();
  const { loading: campaignLoading, campaign } = useContext(CampaignContext);
  const { error, data, loading } = useSellerGroupQuery({
    skip: !sellerGroupSecret || !campaign,
    variables: {
      slug: campaign?.slug,
      secret: sellerGroupSecret || '',
    },
  });
  const [translate] = useTranslation();
  const [translatedError, setTranslatedError] = useState<string | undefined>();
  const locale = campaign?.organisation?.locale;

  useEffect(() => {
    const { message } = error || { message: 'COMMON.ERROR_MESSAGE' };
    const API_ERROR_PREFIX = 'API_ERRORS';
    const toTranslate = message.includes(':') ? [
      API_ERROR_PREFIX,
      message?.split(':')
        ?.pop()
        ?.trim(),
    ].join('.') : message;
    setTranslatedError(toTranslate);
  }, [error]);

  const onCopy = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (loading || campaignLoading) return <SellerGroupSkeleton />;

  if (error && translatedError) {
    return (
      <div>
        {translate(translatedError)}
        {' '}
        <Emoji unicode="🧐" />
      </div>
    );
  }

  const sellerGroup = data?.sellerGroup && new SellerGroup(data?.sellerGroup);

  const {
    sellers = [], points, position, offset, orders = [],
  } = sellerGroup || {};

  if (!sellerGroup || !sellers?.length) {
    return <>Ongeldige configuratie voor dit lid. Neem contact op met Trooper om je verder te helpen.</>;
  }

  const DeliveryMethods : Record<DeliveryMethod, { icon: string; label: string; }> = {
    [DeliveryMethod.Deliver]: {
      icon: '📦',
      label: 'COMMON.DELIVERY_METHOD.DELIVER',
    },
    [DeliveryMethod.EatIn]: {
      icon: '🍽',
      label: 'COMMON.DELIVERY_METHOD.EAT_IN',
    },
    [DeliveryMethod.Collect]: {
      icon: '🛍',
      label: 'COMMON.DELIVERY_METHOD.COLLECT',
    },
  };

  return (
    <>
      <Header openCartModal={undefined} />

      <Container className="sellergroup-container my-5">
        <div className="d-flex justify-content-between">
          <h1>
            <Trans
              i18nKey="COMPONENTS.SELLER_GROUP.GREETING"
              values={{ name: sellerGroup.firstNames() }}
              tOptions={{ interpolation: { escapeValue: false } }}
            >
              Hey
              {' '}
              {sellerGroup.firstNames()}
              !
            </Trans>
          </h1>
          <ConfirmRemoveModal sellerGroup={data?.sellerGroup} slug={campaign.slug} />
        </div>

        {position && sellers && campaign?.showScoreboard
      && (
        <p>
          {translate('COMPONENTS.SELLER_GROUP.SELLER_POINTS', {
            count: sellers.length,
            position: position || 0,
            points: points || 0,
          })}
          {!offset ? null : translate('COMPONENTS.SELLER_GROUP.OFFSET', { count: sellers.length, offset })}
          <br />
          <NavLink to={`/${campaign.slug}/?scoreboard=true`}>
            {translate('COMPONENTS.SELLER_GROUP.NAV_LINK_TO_SCOREBOARD')}
          </NavLink>
        </p>
      )}

        <p>
          <Trans i18nKey="COMPONENTS.SELLER_GROUP.PERSONAL_LINK" values={{ count: sellers.length }}>
            Dit is
            {' '}
            {sellers.length > 1 ? 'jullie' : 'jouw'}
            {' '}
            persoonlijke link die je kan delen met familie en vrienden.
          </Trans>
          {campaign?.showScoreboard && (
            <>
              <br />
              <Trans i18nKey="COMPONENTS.SELLER_GROUP.SCOREBOARD_EXPLANATION" values={{ count: sellers.length }}>
                Telkens iemand iets bestelt via deze link, komen er punten bij
                {' '}
                {sellers.length > 1 ? 'jullie' : 'jouw'}
                {' '}
                naam te staan in
                {' '}
                <NavLink to={`/${campaign.slug}/?scoreboard=true`}>het scorebord</NavLink>
                .
              </Trans>
            </>
          )}
        </p>

        <div className="input-group mb-3">
          <input
            type="text"
            id="persoonlijke-link"
            value={sellerGroup.orderLink(campaign.slug)}
            className="form-control"
            readOnly
          />
          <div className="input-group-append">
            <CopyToClipboard onCopy={onCopy} text={sellerGroup.orderLink(campaign.slug)}>
              <Button color="outline-secondary" id="copy-button">{translate('COMMON.COPY')}</Button>
            </CopyToClipboard>
            <Tooltip placement="top" isOpen={isCopied} target="copy-button">{translate('COMMON.TEXT_COPIED')}</Tooltip>
          </div>
        </div>

        <p className="mb-5">
          {translate('COMPONENTS.SELLER_GROUP.SHARE_ON_FACEBOOK')}
          <FacebookShareButton link={sellerGroup.orderLink(campaign.slug)} />
        </p>

        {!orders?.length ? <p>{translate('COMPONENTS.SELLER_GROUP.NOBODY_ORDERED')}</p>
          : (
            <>
              <div className="d-flex justify-content-between">
                <p>
                  {translate('COMPONENTS.SELLER_GROUP.GOOD_JOB', { count: sellers.length })}
                </p>
                <Button outline onClick={() => window.print()}>{translate('COMPONENTS.SELLER_GROUP.PRINT')}</Button>
              </div>

              <div>
                {orders.map((o) => (
                  <Card key={o._id} className="p-4 mt-3">
                    <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.NAME_LABEL')}>
                      {o.name}
                    </OrderProperty>
                    <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.PURCHASE_NUMBER_LABEL')}>
                      {o.cardsCount}
                    </OrderProperty>
                    <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.CODE_LABEL')}>
                      {o.code}
                    </OrderProperty>
                    {o.deliveryMethod && (
                      <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.DISTRIBUTION_LABEL')}>
                        <Emoji
                          unicode={DeliveryMethods[o.deliveryMethod].icon}
                          ariaLabel={DeliveryMethods[o.deliveryMethod].label}
                        />
                        {' '}
                        {translate(DeliveryMethods[o.deliveryMethod].label)}
                      </OrderProperty>
                    )}
                    {o.address && (
                      <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.ADDRESS_LABEL')}>
                        <Emoji unicode="🏠" />
                        {' '}
                        {o.address.description}
                      </OrderProperty>
                    )}
                    {o.collectTimeslot && (
                      <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.TIMESLOT_LABEL')}>
                        {formatTimeslot(o.collectTimeslot, locale)}
                      </OrderProperty>
                    )}
                    <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.EMAIL_LABEL')}>
                      <Emoji unicode="✉" aria-label="Email" />
                      {' '}
                      <a href={`mailto:${o.email}`} target="_blank" rel="noopener noreferrer">
                        {o.email}
                      </a>
                    </OrderProperty>
                    {o.datePaid && (
                      <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.DATE_LABEL')}>
                        {o.datePaid ? new Date(o.datePaid).toLocaleDateString(locale, SHORT_DATE_TIME) : ''}
                      </OrderProperty>
                    )}
                    {!!o.customFields?.length && o.customFields.map((c) => {
                      const field = campaign.formFields.find((f) => f._id === c?.formField?._id);
                      return field && c ? (
                        <OrderProperty key={c._id} label={field.label}>
                          {c.value?.join(', ')}
                        </OrderProperty>
                      ) : null;
                    })}
                    <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.QUANTITY_LABEL')}>
                      {o.quantities?.filter(isDefined)?.map(({ quantity, price, article }) => (
                        <Row className="col-offset-auto" key={article.name}>
                          <Col lg={2} className="col-4">{Dinero({ amount: price }).toFormat()}</Col>
                          <Col lg={1} className="col-1">&times;</Col>
                          <Col lg={2} className="col-2">{quantity}</Col>
                          <Col lg="auto" className="col-auto">{article.name}</Col>
                        </Row>
                      ))}
                    </OrderProperty>
                    {o.amountPaid && (
                      <OrderProperty label={translate('COMPONENTS.SELLER_GROUP.TOTAL_LABEL')}>
                        {Dinero({ amount: o.amountPaid }).toFormat()}
                      </OrderProperty>
                    )}
                  </Card>
                ))}
              </div>
            </>
          )}
      </Container>
    </>
  );
};

export default SellerGroupView;
